import { useEffect, useRef, useState } from 'react';
import { listarClientes } from '../api';
import { usuarioLogado, logout } from '../lib/sso';

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('atrio.theme') || 'dark');
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('atrio.theme', theme);
  }, [theme]);
  return [theme, () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))];
}

// Menu organizado por FLUXO DE TRABALHO (referência OneFlow: importar →
// escriturar → fechar → apurar → entregar), com filtro por regime tributário
// e por módulos ativos do cliente:
//   - `regimes`: item só aparece para esses regimes (ausente = todos).
//     Cliente sem regime definido vê tudo (fail-open até o cadastro ser corrigido).
//   - `modulo`: item só aparece se o módulo estiver ativo no cadastro do cliente.
// Itens `wip: true` renderizam página "em construção".
const NAV_GROUPS = [
  {
    label: 'Início',
    items: [
      { id: 'home', label: 'Módulos', icon: '⌂' },
      { id: 'resumo-competencia', label: 'Resumo da Competência', icon: '▦' },
      { id: 'alertas', label: 'Central de alertas', icon: '⚠' },
    ],
  },
  {
    label: 'Contábil',
    modulo: 'modulo_contabil',
    items: [
      { id: 'dashboard',   label: 'Visão geral',         icon: '◐' },
      { id: 'lancamentos', label: 'Lançamentos',         icon: '⊞' },
      { id: 'relatorios',  label: 'Balancete & Relatórios', icon: '📒', tab: 'balancete' },
      { id: 'plano',       label: 'Plano de contas',     icon: '☰' },
      { id: 'fechamento',  label: 'Fechamento do Período', icon: '🔒' },
      { id: 'configuracoes', label: 'Configurações',      icon: '⚙' },
    ],
  },
  {
    label: 'Patrimônio & Folha',
    modulo: 'modulo_contabil',
    items: [
      { id: 'imobilizado',    label: 'Ativo Imobilizado',      icon: '🏛' },
      { id: 'import-dominio', label: 'Importar Domínio Folha', icon: '📥' },
    ],
  },
  {
    label: 'Fiscal — Escrituração',
    modulo: 'modulo_fiscal',
    items: [
      { id: 'nf-escrituracao', label: 'Notas fiscais', icon: '🧾' },
      { id: 'livros-fiscais',  label: 'Livros fiscais',                  icon: '📚', wip: true },
    ],
  },
  {
    label: 'Fiscal — Apurações',
    modulo: 'modulo_fiscal',
    items: [
      { id: 'apuracao-das',        label: 'DAS (Simples Nacional)',  icon: '◇', regimes: ['simples', 'mei'] },
      { id: 'apuracao-pis-cofins', label: 'PIS/COFINS',              icon: '⊟', regimes: ['presumido', 'real'] },
      { id: 'apuracao-irpj-csll',  label: 'IRPJ/CSLL',               icon: '⊠', regimes: ['presumido', 'real'] },
      { id: 'distribuicao-lucros', label: 'Distribuição de lucros',  icon: '💰' },
      { id: 'dividendos',          label: 'Dividendos',              icon: '⚐' },
      { id: 'irpfm',               label: 'IRPFM Anual',             icon: '∑' },
    ],
  },
  {
    label: 'Fiscal — Obrigações Acessórias',
    modulo: 'modulo_fiscal',
    items: [
      { id: 'retencoes',           label: 'EFD-Reinf',               icon: '✦' },
      { id: 'defis',               label: 'DEFIS (anual Simples)',    icon: '◇', wip: true, regimes: ['simples'] },
      { id: 'efd-contribuicoes',   label: 'EFD-Contribuições',       icon: '⊟', wip: true, regimes: ['presumido', 'real'] },
      { id: 'dctfweb',             label: 'DCTFWeb',                  icon: '⊞', wip: true },
      { id: 'mit',                 label: 'MIT (Módulo Inclusão Tributos)', icon: '⊕', wip: true },
      { id: 'ecf',                 label: 'ECF (anual LP/LR)',        icon: '⊠', wip: true, regimes: ['presumido', 'real'] },
    ],
  },
  {
    label: 'Integrações',
    items: [
      { id: 'pendencias',  label: 'Pendências (Finance/Notas)', icon: '⚑' },
      { id: 'mapeamento',  label: 'Mapeamentos',                icon: '⇄' },
      { id: 'integracoes', label: 'Conexões',                   icon: '⚡' },
      { id: 'saude-cross-system', label: 'Saúde Ecossistema',   icon: '✦' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { id: 'clientes',     label: 'Clientes',     icon: '◉' },
      { id: 'certificados', label: 'Certificados', icon: '⊕' },
    ],
  },
];

/**
 * Filtra grupos/itens pelo regime efetivo e módulos do cliente selecionado.
 * Sem cliente ou sem regime definido → mostra tudo (fail-open).
 */
function navParaCliente(cliente) {
  const regime = cliente?.regime_efetivo || cliente?.regime_override || cliente?.regime || null;
  const moduloAtivo = (m) => !m || !cliente || cliente[m] !== false;

  return NAV_GROUPS
    .filter(g => moduloAtivo(g.modulo))
    .map(g => ({
      ...g,
      items: g.items.filter(item => {
        if (!moduloAtivo(item.modulo)) return false;
        if (item.regimes && regime) return item.regimes.includes(regime);
        return true;
      }),
    }))
    .filter(g => g.items.length > 0);
}

// Ecossistema Átrio — paleta unificada gold/void
// Padrão de naming compartilhado entre os 5 sistemas. Manter em sincronia
// com os AppShells/PlatformBars dos outros projetos.
const SISTEMAS = [
  { id: 'gesthub',  label: 'Gesthub',       url: 'https://gesthub.atriocontabilidade.com.br/' },
  { id: 'notas',    label: 'Notas',   url: 'https://notas.atriocontabilidade.com.br/' },
  { id: 'finance',  label: 'Finance', url: 'https://finance.atriocontabilidade.com.br/' },
  { id: 'contabil', label: 'Flow',    url: 'https://contabil.atriocontabilidade.com.br/', active: true },
  { id: 'connect',  label: 'Connect', url: 'https://connect.atriocontabilidade.com.br/' },
];

function soDigitos(v) { return String(v || '').replace(/\D/g, ''); }
function fmtCnpj(v) {
  const d = soDigitos(v);
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return v || '';
}

/**
 * ClientePicker — escolher empresa sem rolar lista cega:
 * busca por nome OU CNPJ, secao de recentes, regime em cada linha, teclado.
 */
function ClientePicker({ clientes, clienteId, onSetClienteId }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [foco, setFoco] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const sel = clientes.find(c => c.id === clienteId) || null;

  const recentes = (() => {
    try { return JSON.parse(localStorage.getItem('flow_clientes_recentes') || '[]'); }
    catch { return []; }
  })();

  const escolher = (c) => {
    onSetClienteId(c.id);
    try {
      const r = [c.id, ...recentes.filter(x => x !== c.id)].slice(0, 5);
      localStorage.setItem('flow_clientes_recentes', JSON.stringify(r));
    } catch {}
    setAberto(false); setBusca('');
  };

  const q = busca.trim().toLowerCase();
  const qd = soDigitos(busca);
  const filtrados = !q ? clientes : clientes.filter(c =>
    (c.razao_social || '').toLowerCase().includes(q) ||
    (c.nome_fantasia || '').toLowerCase().includes(q) ||
    (qd.length >= 3 && soDigitos(c.nr_insc).includes(qd)));
  const listaRecentes = q ? [] : recentes
    .map(id => clientes.find(c => c.id === id)).filter(Boolean);

  useEffect(() => {
    if (!aberto) return;
    const fecha = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fecha);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => document.removeEventListener('mousedown', fecha);
  }, [aberto]);

  useEffect(() => { setFoco(0); }, [busca]);

  const teclado = (e) => {
    if (e.key === 'Escape') { setAberto(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setFoco(f => Math.min(f + 1, filtrados.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFoco(f => Math.max(f - 1, 0)); }
    if (e.key === 'Enter' && filtrados[foco]) { e.preventDefault(); escolher(filtrados[foco]); }
  };

  const Linha = ({ c, ativo }) => (
    <div onClick={() => escolher(c)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer',
        background: ativo ? 'var(--gold-pale)' : 'transparent',
        borderLeft: ativo ? '2px solid var(--gold)' : '2px solid transparent' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', color: 'var(--text-primary, var(--text))' }}>{c.razao_social}</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtCnpj(c.nr_insc)}
        </div>
      </div>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
        color: 'var(--gold)', border: '1px solid var(--gold-pale)', background: 'var(--gold-pale)',
        borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>
        {(c.regime_efetivo || c.regime || 'sem regime')}
      </span>
    </div>
  );

  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 300 }}>
      <button type="button" onClick={() => setAberto(a => !a)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 11,
          padding: '8px 14px', cursor: 'pointer', color: 'var(--text-primary, var(--text))', fontFamily: 'inherit' }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sel ? sel.razao_social : 'Escolher empresa...'}
          </span>
          {sel && <span style={{ display: 'block', fontSize: 10, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {fmtCnpj(sel.nr_insc)} {'\u00b7'} {(sel.regime_efetivo || sel.regime || '').toUpperCase()}
          </span>}
        </span>
        <span style={{ color: 'var(--gold)', fontSize: 11 }}>{aberto ? '\u25b2' : '\u25bc'}</span>
      </button>
      {aberto && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 13,
          boxShadow: '0 12px 40px rgba(0,0,0,.45)', overflow: 'hidden' }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border-strong)' }}>
            <input ref={inputRef} value={busca} onChange={e => setBusca(e.target.value)} onKeyDown={teclado}
              placeholder="Buscar por nome ou CNPJ..."
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
                background: 'var(--surface-2)', border: '1px solid var(--border-strong)',
                color: 'var(--text-primary, var(--text))', fontFamily: 'inherit' }} />
          </div>
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {listaRecentes.length > 0 && (
              <>
                <div style={{ padding: '7px 14px 3px', fontSize: 9.5, fontWeight: 800, letterSpacing: '.12em',
                  color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Recentes</div>
                {listaRecentes.map(c => <Linha key={'r' + c.id} c={c} ativo={false} />)}
                <div style={{ padding: '7px 14px 3px', fontSize: 9.5, fontWeight: 800, letterSpacing: '.12em',
                  color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Todas ({clientes.length})</div>
              </>
            )}
            {filtrados.length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Nenhuma empresa encontrada para "{busca}".
              </div>
            )}
            {filtrados.slice(0, 60).map((c, i) => <Linha key={c.id} c={c} ativo={i === foco} />)}
            {filtrados.length > 60 && (
              <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--text-secondary)' }}>
                +{filtrados.length - 60} empresas {'\u2014'} refine a busca
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const WORKSPACES = [
  { id: 'inicio',       label: 'Início',       icon: '⌂' },
  { id: 'contabil',     label: 'Contábil',     icon: '📒' },
  { id: 'fiscal',       label: 'Fiscal',       icon: '🧾' },
  { id: 'patrimonio',   label: 'Patrimônio',   icon: '🏛' },
  { id: 'integracoes',  label: 'Integrações',  icon: '⚡' },
  { id: 'admin',        label: 'Admin',        icon: '⚙' },
];
const wsDoGrupo = (label) =>
  label.startsWith('Fiscal') ? 'fiscal'
  : label === 'Contábil' ? 'contabil'
  : label === 'Patrimônio & Folha' ? 'patrimonio'
  : label === 'Integrações' ? 'integracoes'
  : label === 'Administração' ? 'admin'
  : 'inicio';

// Telas cujo conteudo e um documento longo: o menu recolhe para dar largura.
const PAGINAS_DENSAS = new Set([
  'relatorios', 'lancamentos', 'plano', 'nf-escrituracao', 'livros-fiscais',
]);

/**
 * Quem esta logado, do token, com sair ao lado. Sem token nao aparece nada: o
 * bootstrapAuth ja manda para o SSO antes da tela montar.
 */
function UsuarioBarra() {
  const u = usuarioLogado();
  if (!u?.email) return null;
  const papeis = u.papeis.length ? u.papeis.join(', ') : null;
  return (
    <div className="topbar__user" title={papeis ? `Perfil: ${papeis}` : undefined}>
      <span>{'\u2709'} {u.nome || u.email}</span>
      <button className="ghost" onClick={logout} title="Encerrar a sessão neste navegador"
        style={{ padding: '2px 8px', fontSize: 11, borderRadius: 8 }}>
        Sair
      </button>
    </div>
  );
}

export default function AppShell({ page, onNavigate, clienteId, onSetClienteId, children, badges = {} }) {
  const [clientes, setClientes] = useState([]);
  const [ws, setWs] = useState(() => {
    try { return localStorage.getItem('flow_ws') || 'inicio'; } catch { return 'inicio'; }
  });
  const irParaWs = (id) => { setWs(id); try { localStorage.setItem('flow_ws', id); } catch {} };
  useEffect(() => {
    // Navegou para pagina de outro workspace (ex.: CTA do cockpit)? Acompanha.
    for (const g of NAV_GROUPS) {
      if (g.items.some(i => i.id === page)) {
        const w = wsDoGrupo(g.label);
        if (w !== ws) irParaWs(w);
        break;
      }
    }
  }, [page]);
  const [loading, setLoading] = useState(true);
  const [theme, toggleTheme] = useTheme();

  // null = automatico pela tela. Clicar no botao decide so para a tela atual;
  // trocar de tela volta ao automatico.
  const [trilhaManual, setTrilhaManual] = useState(null);
  useEffect(() => { setTrilhaManual(null); }, [page]);
  const trilha = trilhaManual === null ? PAGINAS_DENSAS.has(page) : trilhaManual;
  // Espiar = trilha aberta por cima do conteudo enquanto o mouse esta nela.
  // O grid nao muda, entao o relatorio nao reflui a cada passada de mouse.
  const [espiando, setEspiando] = useState(false);
  const aberto = !trilha || espiando;

  useEffect(() => {
    // Antes filtrava reinf=true (heranca da fase so-REINF): cliente escriturado
    // sem obrigacao de REINF (ex.: BFA) sumia do seletor. Agora entra qualquer
    // cliente com algum modulo do Flow ligado.
    listarClientes()
      .then(r => {
        // No Flow so aparece quem foi ATIVADO em algum modulo (tela Clientes).
        // reinf_obrigado nao entra: vem ligado do Gesthub p/ quase todo mundo e
        // deixava a carteira inteira no seletor.
        const uteis = (r.items || []).filter(c =>
          c.modulo_contabil || c.modulo_fiscal || c.modulo_relatorios);
        uteis.sort((a, b) => (a.razao_social || '').localeCompare(b.razao_social || '', 'pt-BR'));
        setClientes(uteis);
        // Nao escolhemos empresa por ninguem. Abrir ja dentro da primeira da
        // lista faz trabalhar no cliente errado por inercia.
      })
      .catch(err => console.error('Erro listar clientes:', err.message))
      .finally(() => setLoading(false));
  }, []);

  const clienteSel = clientes.find(c => c.id === clienteId) || null;
  const navGroups = navParaCliente(clienteSel);
  const regimeEfetivo = clienteSel?.regime_efetivo || clienteSel?.regime || null;

  return (
    <div className={`shell ${trilha ? 'shell--trilha' : ''} ${trilha && espiando ? 'shell--espiando' : ''}`}>
      <nav className="platform-bar">
        {SISTEMAS.map((s) => (
          <a
            key={s.id}
            href={s.active ? '#' : (s.url || '#')}
            className={`platform-bar__tab ${s.active ? 'platform-bar__tab--active' : ''}`}
            {...(!s.active && s.url ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            onClick={s.active ? (e) => e.preventDefault() : undefined}
          >
            <span className="platform-bar__dot" />
            {s.label}
          </a>
        ))}
      </nav>

      <header className="topbar">
        <div className="topbar__brand">
          <div className="topbar__brand-mark" title="Átrio">
            <svg width="24" height="28" viewBox="0 0 40 46" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M4 44V18C4 9.16 11.16 2 20 2C28.84 2 36 9.16 36 18V44" stroke="#16110C" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M13 44V24C13 20.13 16.13 17 20 17C23.87 17 27 20.13 27 24V44" stroke="#3A2A1C" strokeWidth="2" fill="none" opacity="0.55" strokeLinecap="round" />
            </svg>
          </div>
          <div className="topbar__brand-text">
            <span className="topbar__wm">Átrio Flow</span>
            <span className="topbar__bsep">|</span>
            <span className="topbar__bsub">Motor de produção contábil-fiscal</span>
          </div>
        </div>
        <div className="topbar__right">
          <div className="topbar__cliente-picker">
            {loading
              ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '10px 14px' }}>Carregando empresas...</div>
              : clientes.length === 0
                ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 14px',
                    border: '1px dashed var(--border-strong)', borderRadius: 10 }}>
                    Nenhuma empresa ativada no Flow {'—'} ative em Admin {'›'} Clientes
                  </div>
                : <ClientePicker clientes={clientes} clienteId={clienteId} onSetClienteId={onSetClienteId} />}
          </div>
          <button className="ghost" onClick={toggleTheme} title={`Mudar para tema ${theme === 'dark' ? 'claro' : 'escuro'}`}>
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
          <UsuarioBarra />
        </div>
      </header>

      <aside className="sidebar" onMouseEnter={() => setEspiando(true)}
        onMouseLeave={() => setEspiando(false)}>
        <button type="button" className="sidebar__trilha-btn"
          onClick={() => setTrilhaManual(!trilha)}
          title={trilha ? 'Expandir menu' : 'Recolher menu e dar mais largura ao documento'}>
          <span>{trilha ? '\u00bb' : '\u00ab'}</span>
          <span className="sidebar__label">{trilha ? 'Fixar menu aberto' : 'Recolher menu'}</span>
        </button>
        {clienteSel && !regimeEfetivo && aberto && (
          <div className="sidebar__aviso-regime" style={{
            margin: '8px 10px', padding: '8px 10px', borderRadius: 6, fontSize: 11,
            background: 'rgba(234,179,8,0.10)', border: '1px solid rgba(234,179,8,0.35)', color: '#eab308',
          }}>
            ⚠ Cliente sem regime definido — menu completo exibido. Configure em Clientes.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: aberto ? '1fr 1fr' : '1fr', gap: 6,
          padding: aberto ? '10px 10px 12px' : '4px 2px 10px',
          borderBottom: '1px solid var(--border-strong)', marginBottom: 8 }}>
          {WORKSPACES.map(w => (
            <button key={w.id} type="button" onClick={() => irParaWs(w.id)} title={w.label}
              style={{ display: 'flex', alignItems: 'center',
                justifyContent: aberto ? 'flex-start' : 'center',
                gap: aberto ? 6 : 0, padding: aberto ? '7px 9px' : '8px 0',
                borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: aberto ? 11.5 : 15,
                fontWeight: ws === w.id ? 700 : 500, textAlign: 'left',
                border: ws === w.id ? '1px solid var(--gold)' : '1px solid var(--border-strong)',
                background: ws === w.id ? 'var(--gold-pale)' : 'transparent',
                color: ws === w.id ? 'var(--gold)' : 'var(--text-secondary)' }}>
              <span>{w.icon}</span>
              {aberto && <span>{w.label}</span>}
            </button>
          ))}
        </div>
        {navGroups.filter(g => wsDoGrupo(g.label) === ws).map(group => (
          <div key={group.label}>
            <div className="sidebar__section">{group.label}</div>
            {group.items.map(item => (
              <div
                key={item.id}
                className={`sidebar__item ${page === item.id ? 'sidebar__item--active' : ''} ${item.wip ? 'sidebar__item--wip' : ''}`}
                onClick={() => {
                  if (item.tab) { try { sessionStorage.setItem('flow_relatorios_tab', item.tab); } catch {} }
                  onNavigate(item.id);
                }}
                title={item.wip ? `${item.label} — em construção` : item.label}
              >
                <span className="sidebar__icon">{item.icon}</span>
                <span className="sidebar__label">{item.label}</span>
                {item.wip && <span className="sidebar__wip-tag">soon</span>}
                {badges[item.id] > 0 && <span className="sidebar__badge">{badges[item.id]}</span>}
              </div>
            ))}
          </div>
        ))}
      </aside>

      <main className="main">{children}</main>
    </div>
  );
}
